import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FcmService {
  private isBrowser: boolean;
  private cachedToken: string | null = null;
  private isRequesting = false;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private http: HttpClient
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  /**
   * Check if push notifications are supported by the client browser
   */
  isSupported(): boolean {
    if (!this.isBrowser) return false;
    return (
      typeof window !== 'undefined' &&
      'Notification' in window &&
      'serviceWorker' in navigator &&
      'PushManager' in window
    );
  }

  /**
   * Current browser notification permission state ('granted' | 'denied' | 'default')
   */
  get permission(): NotificationPermission | 'unsupported' {
    if (!this.isSupported()) return 'unsupported';
    return Notification.permission;
  }

  /**
   * Acquire the FCM device token during login or on-demand.
   * If notifications are denied or unsupported, resolves gracefully with null
   * so the authentication flow is never blocked.
   */
  async getFcmToken(): Promise<string | null> {
    if (!this.isSupported()) {
      return null;
    }

    if (this.cachedToken) {
      return this.cachedToken;
    }

    if (this.isRequesting) {
      return null;
    }

    this.isRequesting = true;

    try {
      // 1. Check or request notification permission
      let perm = Notification.permission;
      if (perm === 'default') {
        perm = await Notification.requestPermission();
      }

      if (perm !== 'granted') {
        this.isRequesting = false;
        return null;
      }

      // 2. Register or retrieve the service worker registration
      let swRegistration: ServiceWorkerRegistration | undefined;
      try {
        swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
          scope: '/'
        });
        await navigator.serviceWorker.ready;
      } catch (swErr) {
        console.warn('[FCM] Service worker registration error:', swErr);
      }

      // 3. Obtain FCM token using Firebase SDK if configured
      const fbConfig = environment.firebase;
      if (fbConfig && fbConfig.apiKey && fbConfig.projectId) {
        try {
          const importApp = new Function("return import('firebase/app')");
          const importMsg = new Function("return import('firebase/messaging')");
          const fbApp = await importApp();
          const fbMsg = await importMsg();

          const app = fbApp.getApps().length > 0 ? fbApp.getApp() : fbApp.initializeApp(fbConfig);
          const messaging = fbMsg.getMessaging(app);

          const token = await fbMsg.getToken(messaging, {
            vapidKey: fbConfig.vapidKey,
            serviceWorkerRegistration: swRegistration
          });

          if (token) {
            this.cachedToken = token;
            this.isRequesting = false;
            return token;
          }
        } catch (fbErr) {
          console.warn('[FCM] Firebase SDK token retrieval warning:', fbErr);
        }
      }

      // 4. Fallback: retrieve native browser push subscription endpoint as token if Firebase config is pending
      if (swRegistration && swRegistration.pushManager) {
        try {
          let subscription = await swRegistration.pushManager.getSubscription();
          if (!subscription && fbConfig?.vapidKey) {
            subscription = await swRegistration.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: this.urlBase64ToUint8Array(fbConfig.vapidKey)
            });
          }
          if (subscription && subscription.endpoint) {
            // Use subscription endpoint as unique device identifier if Firebase SDK is not linked yet
            const deviceToken = `web_push:${btoa(subscription.endpoint).slice(0, 64)}`;
            this.cachedToken = deviceToken;
            this.isRequesting = false;
            return deviceToken;
          }
        } catch (subErr) {
          // Silent fallback
        }
      }

      this.isRequesting = false;
      return null;
    } catch (err) {
      console.warn('[FCM] Error acquiring FCM device token:', err);
      this.isRequesting = false;
      return null;
    }
  }

  /**
   * Sync FCM token with the backend (useful for token refresh or post-login permission grants)
   */
  async syncTokenWithBackend(fcmToken: string): Promise<boolean> {
    if (!fcmToken || !this.isBrowser) return false;

    const backendUrl = environment.backendUrl || '';
    try {
      await firstValueFrom(
        this.http.post(`${backendUrl}/auth/user/fcm-token`, {
          fcm_token: fcmToken,
          device_type: 'web'
        })
      );
      this.cachedToken = fcmToken;
      return true;
    } catch (err) {
      console.warn('[FCM] Could not sync token with backend:', err);
      return false;
    }
  }

  /**
   * Helper to convert VAPID base64 key to Uint8Array
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}
